import { PII_FIELDS, sanitizeUrl } from '@globals/personalIdentifierInfo/pIISanitizeUtils'
import { Provider } from '@rollbar/react'
import { FC, ReactNode } from 'react'
import Rollbar, { Configuration, Dictionary } from 'rollbar'
import { envConfig } from './globals/env'

type RollbarProviderProps = {
  children: ReactNode
}

const ENV: string = envConfig.DEPLOY_ENVIRONMENT
const ROLLBAR_ACCESS_TOKEN: string = envConfig.ROLLBAR_SDK_ID
const APP_VERSION: string = envConfig.APP_VERSION

/** `transform` receives a `Dictionary` (index signature of `unknown`), so narrow before walking. */
const isDictionary = (value: unknown): value is Dictionary =>
  typeof value === 'object' && value !== null

/**
 * Redacts nested PII from the page URL Rollbar attaches to every event.
 *
 * `scrubFields` covers top-level `email=`-style params in request bodies and
 * similar fields. It cannot reach PII nested inside an encoded redirect
 * (`?next=%2Fusers%3Femail%3D…`), so `sanitizeUrl` is applied to the URL fields
 * specifically.
 */
const sanitizeRollbarRequestUrls = (data: Dictionary): void => {
  const request = data.request
  if (!isDictionary(request)) return

  if (typeof request.url === 'string') request.url = sanitizeUrl(request.url)
  if (typeof request.query_string === 'string') {
    request.query_string = sanitizeUrl(request.query_string)
  }
}

const RollBarProvider: FC<RollbarProviderProps> = ({ children }) => {
  const rollbarConfig: Configuration = {
    accessToken: ROLLBAR_ACCESS_TOKEN,
    environment: ENV,
    captureUncaught: true,
    captureUnhandledRejections: false,
    reportLevel: 'warning',
    scrubFields: [...PII_FIELDS],
    // IMPORTANT needed so that default scrub fields from rollbar
    // remain scrubbed
    overwriteScrubFields: false,
    transform: sanitizeRollbarRequestUrls,
    payload: {
      client: {
        javascript: {
          code_version: APP_VERSION,
          source_map_enabled: true,
          guess_uncaught_frames: true,
        },
      },
    },
  }

  const rollbar = new Rollbar(rollbarConfig)
  return <Provider instance={rollbar}>{children}</Provider>
}

export default RollBarProvider
